import { useState, useEffect } from "react";
import axios from 'axios';
import AppURL from "../components/AppUrl";

interface UserData {
    name: string;
    email: string;
    id: string;
    role: string;
}

interface StockItem {
    _id: string;
    name: string;
    quantity: number;
    category: string;
    qunatiteConsomee: number;
    createdAt: Date;
}

const GestionStock = () => {
    const [items, setItems] = useState<StockItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showTakeModal, setShowTakeModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
    const [quantityToTake, setQuantityToTake] = useState(1);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [itemHistory, setItemHistory] = useState<any[]>([]);
    const [newItem, setNewItem] = useState({
        name: "",
        quantity: "",
        category: "",
        createdAt: "",
    });

    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateItemData, setUpdateItemData] = useState({
        id: '',
        name: '',
        quantity: 0,
        category: ''
    });

    const handleDeleteItem = async () => {
        try {
            await axios.delete(`${AppURL}/api/stocks/${selectedItem?._id}`);
            setShowDeleteConfirmModal(false);
            setShowHistoryModal(false);
            setSelectedItem(null);
            fetchItems(); // Rafraîchir la liste
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            setError("Erreur lors de la suppression de l'item");
        }
    };

    const handleUpdateItem = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.put(`${AppURL}/api/stocks/${updateItemData.id}`, {
                name: updateItemData.name,
                quantity: updateItemData.quantity,
                category: updateItemData.category
            });
            setShowUpdateModal(false);
            setShowHistoryModal(false);
            setSelectedItem(null);
            fetchItems(); // Rafraîchir la liste
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
            setError("Erreur lors de la modification de l'item");
        }
    };

    // Charger les items et l'utilisateur au montage du composant
    useEffect(() => {
        fetchItems();

        // Récupérer les données utilisateur depuis le localStorage
        const userData = localStorage.getItem('userData');
        if (userData) {
            setCurrentUser(JSON.parse(userData));
        }
    }, []);

    const fetchItemHistory = async (itemId: string) => {
        try {
            const response = await axios.get(`${AppURL}/api/assignments/by-item/${itemId}`);
            setItemHistory(response.data);
        } catch (error) {
            console.error("Erreur lors de la récupération de l'historique:", error);
            setError("Erreur lors du chargement de l'historique");
        }
    };

    const fetchItems = async () => {
        try {
            const response = await axios.get(`${AppURL}/api/stocks/getAll`);
            setItems(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Erreur lors de la récupération des items:", error);
            setError("Erreur lors du chargement du stock");
            setLoading(false);
        }
    };

    const handleTakeItem = async () => {
        if (!selectedItem || !currentUser) return;

        try {
            // 1. Mettre à jour l'item (réduire la quantité)
            await axios.put(`${AppURL}/api/stocks/${selectedItem._id}`, {
                qunatiteConsomee: selectedItem.qunatiteConsomee + quantityToTake
            });

            // 2. Créer l'assignment
            await axios.post(`${AppURL}/api/assignments/create`, {
                nomEmploye: currentUser.name,
                itemId: selectedItem._id,
                nomDitem: selectedItem.name,
                quantite: quantityToTake,
                date: new Date().toISOString()
            });

            // Rafraîchir la liste
            fetchItems();
            setShowTakeModal(false);
            setQuantityToTake(1);
        } catch (error) {
            console.error("Erreur lors de la prise d'item:", error);
            setError("Erreur lors de la prise d'item");
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewItem(prev => ({
            ...prev,
            [name]: name === "quantity" ? parseInt(value) || 0 : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const itemToCreate = {
                ...newItem,
                createdAt: new Date().toISOString(),
            };

            await axios.post(`${AppURL}/api/stocks/create`, itemToCreate);
            setShowModal(false);
            setNewItem({
                name: "",
                quantity: '',
                category: "",
                createdAt: ""
            });
            fetchItems();
        } catch (error) {
            console.error("Erreur lors de la création:", error);
            setError("Erreur lors de la création de l'item");
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-3">Chargement du stock...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
                <p>{error}</p>
                <button
                    onClick={fetchItems}
                    className="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className=" mx-auto px-4 py-8 relative min-h-screen mt-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Stock</h1>

            {/* Liste des items */}
            <div className="bg-white shadow-md rounded-lg overflow-x-auto mb-20">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité Restante</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {items.length > 0 ? (
                            items.map((item) => (
                                <tr
                                    key={item._id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setShowTakeModal(true);
                                    }}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{item.quantity - item.qunatiteConsomee}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{item.quantity}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 capitalize">{item.category}</div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                                    Aucun item en stock
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal pour prendre un item */}
            {showTakeModal && selectedItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Prendre {selectedItem.name}
                            </h2>
                            <button
                                onClick={() => setShowTakeModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                Quantité disponible: <span className="font-semibold">{selectedItem.quantity - selectedItem.qunatiteConsomee}</span>
                            </p>
                            <label htmlFor="quantityToTake" className="block text-sm font-medium text-gray-700 mb-1">
                                Quantité à prendre *
                            </label>
                            <input
                                type="number"
                                id="quantityToTake"
                                max={selectedItem.quantity - selectedItem.qunatiteConsomee}
                                value={quantityToTake}
                                onChange={(e) => {
                                    const value = Math.max(1, Number(e.target.value));
                                    setQuantityToTake(value);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>

                        {currentUser && (
                            <div className="mb-4">
                                <p className="text-sm text-gray-600">
                                    Vous prenez cet item en tant que: <span className="font-semibold">{currentUser.name}</span>
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowTakeModal(false);
                                    setQuantityToTake(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                            >
                                Annuler
                            </button>

                            {currentUser?.role === 'manager' && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowHistoryModal(true);
                                        fetchItemHistory(selectedItem._id);
                                    }}
                                    className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 w-full sm:w-auto"
                                >
                                    Paramètres
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={handleTakeItem}
                                disabled={quantityToTake <= 0 || quantityToTake > selectedItem.quantity - selectedItem.qunatiteConsomee}
                                className={`px-4 py-2 rounded-md text-white w-full sm:w-auto ${quantityToTake <= 0 || quantityToTake > selectedItem.quantity - selectedItem.qunatiteConsomee
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                Confirmer la prise
                            </button>
                        </div>


                    </div>
                </div>
            )}



            {/* Modal d'historique pour les managers */}
            {showHistoryModal && selectedItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Historique pour {selectedItem.name}
                            </h2>
                            <button
                                onClick={() => setShowHistoryModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-4">
                            <h3 className="font-medium text-gray-700 mb-2">Détails de l'item</h3>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-sm text-gray-600">Nom</p>
                                    <p className="font-medium">{selectedItem.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Quantité initiale</p>
                                    <p className="font-medium">{selectedItem.quantity}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Quantité consommée</p>
                                    <p className="font-medium">{selectedItem.qunatiteConsomee || 0}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Quantité restante</p>
                                    <p className="font-medium">{selectedItem.quantity - (selectedItem.qunatiteConsomee || 0)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Catégorie</p>
                                    <p className="font-medium capitalize">{selectedItem.category}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Date de Creation</p>
                                    <p className="font-medium capitalize">{new Date(selectedItem.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <h3 className="font-medium text-gray-700 mb-2">Historique des prélèvements</h3>
                            {itemHistory.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantité</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {itemHistory.map((assignment) => (
                                                <tr key={assignment._id}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{assignment.nomEmploye}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">{assignment.quantite}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {new Date(assignment.date).toLocaleDateString('fr-FR')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">Aucun prélèvement enregistré pour cet item</p>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                            <button
                                onClick={() => {
                                    setShowHistoryModal(false);
                                    setShowDeleteConfirmModal(true);
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 w-full sm:w-auto"
                            >
                                Supprimer l'item
                            </button>

                            <button
                                onClick={() => {
                                    setShowHistoryModal(false);
                                    setShowUpdateModal(true);
                                    setUpdateItemData({
                                        id: selectedItem._id,
                                        name: selectedItem.name,
                                        quantity: selectedItem.quantity,
                                        category: selectedItem.category
                                    });
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 w-full sm:w-auto"
                            >
                                Modifier l'item
                            </button>

                            <button
                                onClick={() => setShowHistoryModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {showDeleteConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                        <p className="mb-6">Êtes-vous sûr de vouloir supprimer définitivement cet item ?</p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteConfirmModal(false);
                                    setShowHistoryModal(true);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDeleteItem}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Confirmer la suppression
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de modification */}
            {showUpdateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">Modifier l'item</h2>
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleUpdateItem}>
                            <div className="mb-4">
                                <label htmlFor="updateName" className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                <input
                                    type="text"
                                    id="updateName"
                                    name="name"
                                    value={updateItemData.name}
                                    onChange={(e) => setUpdateItemData({ ...updateItemData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="updateQuantity" className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                                <input
                                    type="number"
                                    id="updateQuantity"
                                    name="quantity"
                                    min="0"
                                    value={updateItemData.quantity}
                                    onChange={(e) => setUpdateItemData({ ...updateItemData, quantity: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="updateCategory" className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                                <select
                                    id="updateCategory"
                                    name="category"
                                    value={updateItemData.category}
                                    onChange={(e) => setUpdateItemData({ ...updateItemData, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Sélectionner une catégorie</option>
                                    <option value="materiel">Matériel</option>
                                    <option value="outillage">Outillage</option>
                                    <option value="consommable">Consommable</option>
                                    <option value="autre">Autre</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowUpdateModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}


            {/* Bouton flottant pour ajouter un item */}
            <button
                onClick={() => setShowModal(true)}
                className="fixed bottom-8 right-8 bg-gray-900 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 transform hover:scale-110"
                aria-label="Ajouter un item"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
            </button>

            {/* Modal de création */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-800">Ajouter un nouvel item</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={newItem.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                                <input
                                    type="number"
                                    id="quantity"
                                    name="quantity"
                                    value={newItem.quantity}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                                <select
                                    id="category"
                                    name="category"
                                    value={newItem.category}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Sélectionner une catégorie</option>
                                    <option value="materiel">Matériel</option>
                                    <option value="outillage">Outillage</option>
                                    <option value="consommable">Consommable</option>
                                    <option value="autre">Autre</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionStock;